import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Car, Navigation, Trash2, MapPin, AlertTriangle, Loader2, RefreshCw, Info, UserCog, PlayCircle, HelpCircle, ChevronRight, Bell } from "lucide-react";
import NotificationsPill from "./NotificationsPill";
import ParkingClock from "./ParkingClock";
import {
  getCarLocation,
  saveCarLocation,
  clearCarLocation,
  formatRelativeTime,
  isStaleSave,
  distanceMeters,
  formatDistance,
  buildNavigationUrl,
} from "../lib/carLocation";
import { reportSaveCarEvent } from "../lib/campaign";
import { track } from "../lib/analytics";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

/**
 * Bottom sheet — "Save my car location".
 * MVP scope:
 *  - localStorage only, no backend
 *  - GPS asked only after user taps "Save my car"
 *  - Empty / Saved states in one sheet
 *  - Single stale warning when saved_at > 24 h ago
 *  - No map pin (kept out of scope)
 */
export default function CarLocationSheet({ open, onClose, userPos, onSaved, onOpenDriverDetails, onReplayIntro, onReplayTips }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  const [record, setRecord] = React.useState(null);
  const [note, setNote] = React.useState("");
  const [locating, setLocating] = React.useState(false);
  const [error, setError] = React.useState(null); // { code, message }

  // Refresh state whenever the sheet opens.
  React.useEffect(() => {
    if (open) {
      const r = getCarLocation();
      setRecord(r);
      setNote(r?.note || "");
      setError(null);
      setLocating(false);
    }
  }, [open]);

  const requestSave = React.useCallback(() => {
    if (!("geolocation" in navigator)) {
      setError({
        code: "unsupported",
        message: "Your browser doesn't support location. Try a different browser.",
      });
      return;
    }
    setLocating(true);
    setError(null);

    const commit = (lat, lng, accuracy_m) => {
      // Never persist invalid coordinates.
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        setLocating(false);
        setError({
          code: "unavailable",
          message: "We couldn't read your position. Try again outdoors.",
        });
        return;
      }
      try {
        const saved = saveCarLocation({ lat, lng, accuracy_m, note });
        setRecord(saved);
        setNote(saved.note || "");
        setLocating(false);
        // Fire-and-forget campaign event (privacy-safe: no coords, no note).
        reportSaveCarEvent();
        track("save_parking_saved");
        onSaved?.();
      } catch (e) {
        setError({
          code: "storage_blocked",
          message: "We couldn't save your car — storage is blocked in this browser.",
        });
        setLocating(false);
      }
    };

    // Map-provided position fallback (already-fresh from App.jsx's watchPosition).
    const usableUserPos =
      userPos &&
      Number.isFinite(userPos.lat) &&
      Number.isFinite(userPos.lng) &&
      (!Number.isFinite(userPos.accuracy) || userPos.accuracy < 500);

    // Stage 3 (final fallback): use the map's live position, or fail with a distinct message.
    const finalFallback = (code, message) => {
      if (usableUserPos) {
        commit(userPos.lat, userPos.lng, Number.isFinite(userPos.accuracy) ? userPos.accuracy : null);
      } else {
        setLocating(false);
        setError({ code, message });
      }
    };

    // Stage 2: low-accuracy retry with a longer timeout.
    const stage2 = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          commit(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
        },
        (err) => {
          if (err.code === 1) {
            setLocating(false);
            setError({
              code: "denied",
              message: "Location access is blocked. Enable it in your browser settings and try again.",
            });
            return;
          }
          if (err.code === 2) {
            finalFallback(
              "unavailable",
              "We couldn't read your position. Try again outdoors."
            );
            return;
          }
          // timeout or unknown → last fallback to the map's live position
          finalFallback(
            "timeout",
            "Location took too long. Try again outdoors, or move somewhere with a clearer sky."
          );
        },
        { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
      );
    };

    // Stage 1: try high-accuracy first with a short timeout, but accept cached fixes up to 30s old.
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        commit(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
      },
      (err) => {
        if (err.code === 1) {
          // Permission denied — do NOT retry; user must unblock in settings.
          setLocating(false);
          setError({
            code: "denied",
            message: "Location access is blocked. Enable it in your browser settings and try again.",
          });
          return;
        }
        // POSITION_UNAVAILABLE or TIMEOUT → try again with degraded accuracy + longer timeout
        stage2();
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, [note, userPos, onSaved]);

  const handleRemove = () => {
    if (typeof window !== "undefined" && !window.confirm("Remove your saved car location?")) return;
    clearCarLocation();
    setRecord(null);
    setNote("");
    setError(null);
  };

  const handleTakeMeThere = () => {
    if (!record) return;
    const url = buildNavigationUrl(record.lat, record.lng);
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      window.location.href = url;
    }
  };

  const hasSave = !!record;
  const stale = hasSave && isStaleSave(record.saved_at);

  // Distance: only show when we have a user position with usable accuracy.
  let distanceLabel = null;
  if (
    hasSave &&
    userPos &&
    Number.isFinite(userPos.lat) &&
    Number.isFinite(userPos.lng) &&
    Number.isFinite(userPos.accuracy) &&
    userPos.accuracy < 200
  ) {
    const meters = distanceMeters(userPos.lat, userPos.lng, record.lat, record.lng);
    distanceLabel = formatDistance(meters);
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[3500] bg-black/75"
            {...dismissProps}
            data-testid="car-location-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[3501] bg-[#141416] border-t border-white/10 rounded-t-3xl p-5 pb-8 max-h-[92vh] overflow-y-auto sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:rounded-3xl sm:border sm:w-[440px] sm:max-w-[92vw]"
            data-testid="car-location-sheet"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                  My Car
                </div>
                <div className="font-heading text-xl font-semibold text-white mt-1 leading-tight" data-testid="car-location-title">
                  {hasSave ? "Where you parked" : "Save your parking"}
                </div>
                <p className="text-[12px] text-zinc-400 mt-1 leading-snug">
                  {hasSave
                    ? "Stored only on this device — no signup, no account."
                    : "Tap Save parking to pin your current spot. Stored on this device only."}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
                data-testid="car-location-close"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            {hasSave ? (
              /* ============ SAVED STATE ============ */
              <div data-testid="car-location-saved-state">
                <div className="p-3 rounded-2xl bg-[#0a0a0c] border border-white/10">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-[#ccff00]/10 border border-[#ccff00]/30 flex items-center justify-center shrink-0">
                      <Car className="w-4 h-4 text-[#ccff00]" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-white" data-testid="car-location-saved-time">
                        Saved {formatRelativeTime(record.saved_at)}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" />
                        <span data-testid="car-location-coords">
                          {record.lat.toFixed(5)}, {record.lng.toFixed(5)}
                        </span>
                        {Number.isFinite(record.accuracy_m) && (
                          <span className="text-zinc-600">· ±{record.accuracy_m} m</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {record.note && (
                    <div className="mt-3 pt-3 border-t border-white/5 text-[12px] text-zinc-300 flex items-start gap-1.5" data-testid="car-location-note">
                      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#ccff00]" />
                      <span>{record.note}</span>
                    </div>
                  )}

                  {distanceLabel && (
                    <div className="mt-3 pt-3 border-t border-white/5 text-[12px] text-zinc-300" data-testid="car-location-distance">
                      {distanceLabel}
                    </div>
                  )}
                </div>

                {stale && (
                  <div className="mt-3 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-100 text-[12px] flex items-start gap-1.5" data-testid="car-location-stale">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Saved more than a day ago — this might be an old parking spot.</span>
                  </div>
                )}

                {error && (
                  <div className="mt-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/40 text-red-200 text-[12px]" data-testid="car-location-error">
                    {error.message}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleTakeMeThere}
                  className="mt-4 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  data-testid="car-location-take-me"
                >
                  <Navigation className="w-4 h-4" />
                  Take me to my car
                </button>

                <button
                  type="button"
                  onClick={requestSave}
                  disabled={locating}
                  className="mt-2 w-full h-11 rounded-full bg-[#0a0a0c] border border-white/15 text-white text-[13px] font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
                  data-testid="car-location-update"
                >
                  {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {locating ? "Updating..." : "Update location"}
                </button>

                <button
                  type="button"
                  onClick={handleRemove}
                  className="mt-2 w-full h-11 rounded-full bg-transparent border border-red-500/30 text-red-200 text-[13px] font-medium inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  data-testid="car-location-remove"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove saved car
                </button>
              </div>
            ) : (
              /* ============ EMPTY STATE ============ */
              <div data-testid="car-location-empty-state">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold inline-flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Note (optional)
                  </span>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 60))}
                    maxLength={60}
                    placeholder="e.g. Level -2, near lift"
                    className="mt-1.5 w-full h-11 px-3 rounded-xl bg-[#0a0a0c] border border-white/10 text-white text-sm outline-none focus:border-[#ccff00]/60 transition-colors"
                    data-testid="car-location-note-input"
                  />
                  <span className="mt-1 block text-[10px] text-zinc-600">{note.length}/60</span>
                </label>

                {error && (
                  <div className="mt-4 p-2.5 rounded-lg bg-red-500/10 border border-red-500/40 text-red-200 text-[12px]" data-testid="car-location-error">
                    {error.message}
                  </div>
                )}

                <button
                  type="button"
                  onClick={requestSave}
                  disabled={locating}
                  className="mt-4 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
                  data-testid="car-location-save"
                >
                  {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />}
                  {locating ? "Getting location..." : "Save parking"}
                </button>

                <div className="mt-3 text-[10px] text-zinc-500 text-center leading-relaxed">
                  We ask for GPS only when you tap Save parking. Nothing is uploaded — the location stays on this device.
                </div>
              </div>
            )}

            {/* Parking clock — countdown + reminder, inside My Car */}
            <div className="mt-4 rounded-xl border border-white/10 overflow-hidden">
              <ParkingClock open={open} />
            </div>

            {/* Settings / help — lightweight, reuses this sheet as the settings area */}
            <div className="mt-5 pt-4 border-t border-white/10 space-y-1">
              <button
                type="button"
                onClick={onOpenDriverDetails}
                className="w-full h-10 px-3 rounded-xl flex items-center gap-2.5 text-left active:bg-white/5"
                data-testid="open-driver-details"
              >
                <UserCog className="w-4 h-4 text-[#ccff00] shrink-0" />
                <span className="text-[13px] text-zinc-200 flex-1">My vehicle details</span>
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </button>
              <div className="w-full px-3 py-2 flex items-center gap-2.5" data-testid="car-reminder-row">
                <Bell className="w-4 h-4 text-[#ccff00] shrink-0" />
                <span className="text-[13px] text-zinc-200 flex-1">Parking reminder</span>
                <NotificationsPill variant="row" />
              </div>
              <button
                type="button"
                onClick={onReplayIntro}
                className="w-full h-10 px-3 rounded-xl flex items-center gap-2.5 text-left active:bg-white/5"
                data-testid="replay-intro"
              >
                <PlayCircle className="w-4 h-4 text-[#ccff00] shrink-0" />
                <span className="text-[13px] text-zinc-200 flex-1">View introduction again</span>
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </button>
              <button
                type="button"
                onClick={onReplayTips}
                className="w-full h-10 px-3 rounded-xl flex items-center gap-2.5 text-left active:bg-white/5"
                data-testid="replay-tips"
              >
                <HelpCircle className="w-4 h-4 text-[#ccff00] shrink-0" />
                <span className="text-[13px] text-zinc-200 flex-1">Show tips again</span>
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
